import { Student, ApiResponse, ImportResult } from '../types';
import { API_ENDPOINTS } from '../config/apiConfig';

export interface PaginatedResponse<T> {
  content: T[];
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  size: number;
  number: number;
}

class StudentService {
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  async getAllStudents(): Promise<ApiResponse<Student[]>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch students');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch students');
    }
  }

  async getStudentsPaginated(page: number = 0, size: number = 10, search?: string): Promise<ApiResponse<PaginatedResponse<Student>>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      
      if (search) {
        params.append('search', search);
      }

      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students/paginated?${params}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch students');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch students');
    }
  }

  async getStudentById(id: string): Promise<ApiResponse<Student>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students/${id}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch student');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch student');
    }
  }

  async getStudentsByUserId(userId: string): Promise<ApiResponse<Student[]>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students?userId=${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch students');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch students');
    }
  }

  async createStudent(studentData: Partial<Student>): Promise<ApiResponse<Student>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(studentData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create student');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to create student');
    }
  }

  async updateStudent(id: string, studentData: Partial<Student>): Promise<ApiResponse<Student>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(studentData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update student');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to update student');
    }
  }

  async deleteStudent(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete student');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to delete student');
    }
  }

  async searchStudents(name: string): Promise<ApiResponse<Student[]>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students/search?name=${encodeURIComponent(name)}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to search students');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to search students');
    }
  }

  async getStudentsBySchool(schoolId: string): Promise<ApiResponse<Student[]>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students/school/${schoolId}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch students');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch students');
    }
  }

  async validateImport(file: File): Promise<ApiResponse<ImportResult>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers: HeadersInit = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students/import/validate`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        // If the response contains validation data, return it even if not ok
        if (data.data && data.data.errors) {
          return data;
        }
        throw new Error(data.error || data.message || 'Failed to validate import');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to validate import');
    }
  }

  async executeImport(file: File): Promise<ApiResponse<ImportResult>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers: HeadersInit = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students/import/execute`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        // If the response contains import data with errors, return it even if not ok
        if (data.data && data.data.errors) {
          return data;
        }
        throw new Error(data.error || data.message || 'Failed to execute import');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to execute import');
    }
  }

  async exportStudentsBySchool(schoolId: string): Promise<Blob> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/students/school/${schoolId}/export`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export students');
      }

      return await response.blob();
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to export students');
    }
  }
}

const studentService = new StudentService();
export default studentService;
